import { useState, useEffect, useMemo, useRef } from "react";
import { useDialog, useOnboarding } from "../components/DialogManager.tsx";
import { CircleMenu } from "../components/widgets/CircleMenu.tsx";
import { CircleMenuItem } from "../components/widgets/CircleMenuItem.tsx";
import { WorkoutList } from "../components/workouts/WorkoutList.tsx";
import { RoutineList } from "../components/routines/RoutineList.tsx";
import { InlineDatePicker } from "../components/inputs/InlineDatePicker.tsx";
import { TbHeartCheck, TbHeartPlus } from "react-icons/tb";
import { PiFolderBold, PiFolderPlusBold } from "react-icons/pi";
import {
  LuMessageCircleHeart,
  LuNotebook,
  LuNotebookPen,
} from "react-icons/lu";
import {
  useExercises,
  useWorkouts,
  useWorkoutRoutines,
  useSharedWorkoutRoutines,
  useActivityFeed,
} from "../api/hooks.ts";
import { AnimatedLoading } from "../components/widgets/AnimatedLoading.tsx";
import {
  User,
  WorkoutRoutine,
  Exercise,
  MUSCLE_GROUP_LABELS,
  SharedWorkoutRoutine,
} from "../api/types.ts";
import { useNavigate, useLocation } from "react-router-dom";
import ExerciseLibrary from "../components/ExerciseLibrary.tsx";
import { TextInput } from "../components/inputs/TextInput.tsx";
import { isNotNullish } from "../util/typeGuards.ts";
import { Button } from "../components/widgets/Button.tsx";
import { OnboardingDialogStep } from "../components/dialogs/OnboardingDialog.tsx";
import { Banner } from "../components/widgets/Banner.tsx";
import { SharedRoutineList } from "../components/sharedRoutines/SharedRoutineList.tsx";
import { IoPeopleOutline } from "react-icons/io5";
import { Spinner } from "../components/widgets/Spinner.tsx";
import { ActivityFeed } from "../components/activity/ActivityFeed.tsx";
import { ToggleButton } from "../components/widgets/ToggleButton.tsx";

interface HomePageProps {
  profile?: User;
  profileLoading?: boolean;
  profileError: Error | null;
  friends: Partial<User>[] | undefined;
}

const HomePage: React.FC<HomePageProps> = ({
  profile,
  profileLoading,
  profileError,
  friends,
}) => {
  const { openDialog, currentDialog } = useDialog();
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab =
    location.pathname === "/exercises"
      ? "exercises"
      : location.pathname === "/workouts"
        ? "workouts"
        : location.pathname === "/routines"
          ? "routines"
          : location.pathname === "/dashboard"
            ? "dashboard"
            : location.pathname === "/activity"
              ? "activity"
              : "workouts";

  // Default to last 14 days
  const getDefaultDates = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 14);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    return { from: sevenDaysAgo, to: today };
  };

  const [workoutDateRange, setWorkoutDateRange] = useState(getDefaultDates());
  const [currentWorkoutPage, setCurrentWorkoutPage] = useState(1);
  const pageSize = 10;
  const [workoutStatus, setWorkoutStatus] = useState<"active" | undefined>(
    undefined,
  );

  // Reset page when date range changes
  useEffect(() => {
    setCurrentWorkoutPage(1);
  }, [workoutDateRange.from, workoutDateRange.to]);

  const {
    data: workoutsResponse,
    isLoading: loadingWorkouts,
    error: workoutsError,
  } = useWorkouts(
    currentTab === "workouts" ? workoutDateRange.from : undefined,
    currentTab === "workouts" ? workoutDateRange.to : undefined,
    currentTab === "workouts" ? currentWorkoutPage : undefined,
    currentTab === "workouts" ? pageSize : undefined,
    currentTab === "workouts" ? workoutStatus : undefined,
  );

  const workouts = workoutsResponse?.workouts || [];
  const workoutPagination = workoutsResponse?.pagination;
  const {
    data: exercises = [],
    isLoading: loadingExercises,
    error: exercisesError,
  } = useExercises();
  const {
    data: routines = [],
    isLoading: loadingRoutines,
    error: routinesError,
  } = useWorkoutRoutines();
  const {
    data: sharedRoutines = [],
    isLoading: loadingSharedRoutines,
    error: sharedRoutinesError,
  } = useSharedWorkoutRoutines();

  const [activityFeedDateRange] = useState(getDefaultDates());
  const [currentActivityFeedPage, setCurrentActivityFeedPage] = useState(1);
  const {
    data: activityFeedResponse,
    isLoading: loadingActivityFeed,
    error: activityFeedError,
  } = useActivityFeed(
    currentTab === "activity" ? activityFeedDateRange.from : undefined,
    currentTab === "activity" ? activityFeedDateRange.to : undefined,
    currentTab === "activity" ? currentActivityFeedPage : undefined,
    currentTab === "activity" ? pageSize : undefined,
  );

  const activityFeed = activityFeedResponse?.activity_feed || [];
  const activityFeedPagination = activityFeedResponse?.pagination;

  const [selectedExercises, setSelectedExercises] = useState<string[]>([]);
  const [routinesSearchQuery, setRoutinesSearchQuery] = useState("");
  const [exercisesSearchQuery, setExercisesSearchQuery] = useState("");

  const [showSharedRoutines, setShowSharedRoutines] = useState(false);

  const { step } = useOnboarding();
  const [showPulse, setShowPulse] = useState(false);

  // Check if user has used the menu before
  useEffect(() => {
    if (currentDialog === "onboarding") {
      return;
    }
    const hasUsedMenu = localStorage.getItem("circle-menu-used");
    if (!hasUsedMenu) {
      setShowPulse(true);
    }
  }, [currentDialog]);

  const queriedRoutines = useMemo(() => {
    const getExerciseName = (exerciseId: string): string => {
      const exercise = exercises.find((ex) => ex.id === exerciseId);
      return exercise ? exercise.name : "Unknown Exercise";
    };

    if (!isNotNullish(routines)) {
      return [];
    }

    if (routinesSearchQuery === "") {
      return routines;
    }

    return routines.filter(
      (routine: WorkoutRoutine) =>
        routine.name
          .toLowerCase()
          .includes(routinesSearchQuery.toLowerCase()) ||
        routine.exercises.some((ex) =>
          getExerciseName(ex.exercise_id)
            .toLowerCase()
            .includes(routinesSearchQuery.toLocaleLowerCase()),
        ),
    );
  }, [routinesSearchQuery, routines, exercises]);

  const queriedSharedRoutines = useMemo(() => {
    const getFriendName = (userId: string): string => {
      if (!friends) {
        return "Unknown Friend";
      }
      const friend = friends.find((friend) => friend.id === userId);
      return friend && (friend.name || friend.email)
        ? (friend.name ?? friend.email!)
        : "Unknown Friend";
    };

    if (!isNotNullish(sharedRoutines)) {
      return [];
    }

    if (routinesSearchQuery === "") {
      return sharedRoutines;
    }

    return sharedRoutines.filter(
      (routine: SharedWorkoutRoutine) =>
        routine.name
          .toLowerCase()
          .includes(routinesSearchQuery.toLowerCase()) ||
        getFriendName(routine.sender_id)
          .toLowerCase()
          .includes(routinesSearchQuery.toLocaleLowerCase()),
    );
  }, [routinesSearchQuery, sharedRoutines, friends]);

  const queriedExercises = useMemo(() => {
    if (!isNotNullish(exercises)) {
      return [];
    }

    if (exercisesSearchQuery === "") {
      return exercises;
    }

    return exercises.filter(
      (exercise: Exercise) =>
        exercise.name
          .toLowerCase()
          .includes(exercisesSearchQuery.toLowerCase()) ||
        exercise.primary_muscle_groups.some((muscle) =>
          MUSCLE_GROUP_LABELS[muscle.muscle_group]
            .toLowerCase()
            .includes(exercisesSearchQuery.toLowerCase()),
        ) ||
        exercise.secondary_muscle_groups.some((muscle) =>
          MUSCLE_GROUP_LABELS[muscle.muscle_group]
            .toLowerCase()
            .includes(exercisesSearchQuery.toLocaleLowerCase()),
        ) ||
        exercise.description
          ?.toLowerCase()
          .includes(exercisesSearchQuery.toLowerCase()),
    );
  }, [exercisesSearchQuery, exercises]);

  // Calculate position based on aspect ratio
  const [menuPosition, setMenuPosition] = useState({
    bottom: "6rem",
    right: "1rem",
  });

  useEffect(() => {
    const updatePosition = () => {
      const aspectRatio = window.innerWidth / window.innerHeight;

      // For portrait/narrow screens (aspect ratio < 1), use smaller distances
      // For landscape/wide screens (aspect ratio >= 1), use larger distances
      if (aspectRatio < 0.75) {
        // Very narrow (like portrait phone)
        setMenuPosition({ bottom: "5rem", right: "0.75rem" });
      } else if (aspectRatio < 1) {
        // Portrait tablet or narrow screen
        setMenuPosition({ bottom: "6rem", right: "1rem" });
      } else if (aspectRatio < 1.5) {
        // Landscape tablet or small desktop
        setMenuPosition({ bottom: "8rem", right: "3rem" });
      } else {
        // Wide desktop
        setMenuPosition({ bottom: "10rem", right: "4rem" });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  const handleExerciseClick = (e: React.MouseEvent, exerciseId: string) => {
    // Don't toggle selection if clicking on the dropdown
    if ((e.target as HTMLElement).closest('[aria-label="Exercise options"]')) {
      return;
    }

    setSelectedExercises((prev) => {
      if (prev.includes(exerciseId)) {
        // Deselect
        return prev.filter((id) => id !== exerciseId);
      } else if (prev.length < 2) {
        // Select (max 2)
        return [...prev, exerciseId];
      }
      // Already at max, don't add
      return prev;
    });
  };

  const MAX_EXERCISES = 30;
  const exerciseCount = exercises?.length || 0;
  const untilExercisesLimit = MAX_EXERCISES - exerciseCount;

  const MAX_ROUTINES = 10;
  const routineCount = routines?.length || 0;
  const untilRoutinesLimit = MAX_ROUTINES - routineCount;

  const exerciseActionsRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`container mx-auto px-4 py-8 relative min-h-[calc(100vh-200px)] ${currentDialog === "onboarding" ? "pointer-events-none" : ""}`}
    >
      <h3 className="mb-8 text-primary text-lg max-h-fit flex flex-row">
        Nice to see you, {profile?.name ? profile.name : <Spinner />}.
      </h3>

      {/* Floating Circle Menu */}
      <div
        className={`fixed z-40 mr-4 ${currentDialog === "onboarding" && step === OnboardingDialogStep.CircleMenu ? "z-[100]" : ""}`}
        style={{ bottom: menuPosition.bottom, right: menuPosition.right }}
      >
        <CircleMenu
          startAngle={-150}
          radius={60}
          showPulse={showPulse}
          display={
            currentDialog === "onboarding" &&
            step === OnboardingDialogStep.CircleMenu
          }
          onOpen={() => {
            // Mark menu as used
            if (showPulse) {
              localStorage.setItem("circle-menu-used", "true");
              setShowPulse(false);
            }
          }}
        >
          <CircleMenuItem
            onClick={() => {
              openDialog("create-exercise", {});
            }}
            label="Add Exercise to Library"
            icon={<PiFolderPlusBold size={24} />}
            disabled={untilExercisesLimit < 1}
          />
          <CircleMenuItem
            onClick={() =>
              openDialog("create-routine", { exercises: exercises })
            }
            label="Create Routine"
            icon={<LuNotebookPen size={20} />}
            disabled={untilRoutinesLimit < 1}
          />
          <CircleMenuItem
            onClick={() =>
              openDialog("record-workout", { exercises: exercises })
            }
            label="Record Workout"
            icon={<TbHeartPlus size={24} />}
          />
        </CircleMenu>
      </div>

      {/* Tab Navigation */}
      <div>
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex flex-row justify-between">
            <nav className="flex flex-row">
              <button
                onClick={() => navigate("/workouts")}
                className={`pb-4 px-2 text-sm font-medium transition-colors flex flex-row items-center gap-2 mx-1 ${
                  currentDialog === "onboarding" &&
                  step === OnboardingDialogStep.Workouts
                    ? "text-secondary border-b-2 border-secondary bg-white pt-2 rounded-t-md z-[100] animate-bounce"
                    : currentDialog !== "onboarding" &&
                        currentTab === "workouts"
                      ? "text-secondary border-b-2 border-secondary"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <TbHeartCheck className="sm:w-4 sm:h-4 w-5 h-5" />{" "}
                <span className="sm:inline-block hidden">Workouts</span>
              </button>
              <button
                onClick={() => navigate("/routines")}
                className={`pb-4 px-2 text-sm font-medium transition-colors flex flex-row items-center gap-2 mx-1 ${
                  currentDialog === "onboarding" &&
                  (step === OnboardingDialogStep.WorkoutRoutines ||
                    step === OnboardingDialogStep.CreateWorkoutFromRoutine)
                    ? "text-secondary border-b-2 border-secondary bg-white pt-2 rounded-t-md z-[100] animate-bounce"
                    : currentDialog !== "onboarding" &&
                        currentTab === "routines"
                      ? `text-secondary border-b-2 border-secondary`
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <LuNotebook className="sm:w-4 sm:h-4 w-5 h-5" />{" "}
                <span className="sm:inline-block hidden">Routines</span>
              </button>
              <button
                onClick={() => navigate("/exercises")}
                className={`flex flex-row items-center gap-2 pb-4 px-2 text-sm font-medium transition-colors mx-1 ${
                  currentDialog === "onboarding" &&
                  step === OnboardingDialogStep.ExerciseLibrary
                    ? "text-secondary border-b-2 border-secondary bg-white pt-2 rounded-t-md z-[100] animate-bounce"
                    : currentDialog !== "onboarding" &&
                        currentTab === "exercises"
                      ? `text-secondary border-b-2 border-secondary`
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <PiFolderBold className="sm:w-4 sm:h-4 w-5 h-5" />{" "}
                <span className="sm:inline-block hidden">Exercise Library</span>
              </button>
              {/* <button
                onClick={() => navigate("/dashboard")}
                className={`pb-4 px-2 text-sm font-medium transition-colors flex flex-row items-center gap-2 mx-1 ${
                  currentTab === "dashboard" && currentDialog !== "onboarding"
                    ? `text-secondary border-b-2 border-secondary`
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <GoGraph className="sm:w-4 sm:h-4 w-5 h-5" />{" "}
                <span className="sm:inline-block hidden">Insights</span>
              </button> */}
            </nav>
            <nav className="flex flex-row">
              <button
                onClick={() => navigate("/activity")}
                className={`flex flex-row items-center gap-2 pb-4 px-2 text-sm font-medium transition-colors mx-1 ${
                  currentDialog === "onboarding" &&
                  step === OnboardingDialogStep.Activity
                    ? "text-secondary border-b-2 border-secondary bg-white pt-2 rounded-t-md z-[100] animate-bounce"
                    : currentDialog !== "onboarding" &&
                        currentTab === "activity"
                      ? `text-secondary border-b-2 border-secondary`
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <LuMessageCircleHeart className="sm:w-4 sm:h-4 w-5 h-5" />{" "}
                <span className="sm:inline-block hidden">Activity</span>
              </button>
            </nav>
          </nav>
        </div>
        <div className={currentDialog === "onboarding" ? "hidden" : ""}>
          {profileLoading ? (
            <AnimatedLoading />
          ) : profileError ? (
            <Banner
              variant="error"
              visible
              title="Error loading user profile"
            />
          ) : currentTab === "workouts" && loadingWorkouts ? (
            <AnimatedLoading />
          ) : currentTab === "workouts" && workoutsError ? (
            <div className="text-red-600">
              <Banner visible variant="error" title="Error loading workouts" />
            </div>
          ) : currentTab === "dashboard" ? (
            <div className="text-gray-600 w-full flex justify-center mt-4 p-2 italic">
              Coming soon
            </div>
          ) : currentTab === "routines" && loadingRoutines ? (
            <AnimatedLoading />
          ) : currentTab === "routines" && routinesError ? (
            <div className="text-red-600">
              <Banner visible variant="error" title="Error loading routines" />
            </div>
          ) : currentTab === "routines" &&
            showSharedRoutines &&
            loadingSharedRoutines ? (
            <AnimatedLoading />
          ) : currentTab === "routines" &&
            showSharedRoutines &&
            sharedRoutinesError ? (
            <div className="text-red-600">
              <Banner
                visible
                variant="error"
                title="Error loading shared routines"
              />
            </div>
          ) : currentTab === "exercises" && loadingExercises ? (
            <AnimatedLoading />
          ) : currentTab === "exercises" && exercisesError ? (
            <div className="text-red-600">
              <Banner visible variant="error" title="Error loading exercises" />
            </div>
          ) : currentTab === "activity" && loadingActivityFeed ? (
            <AnimatedLoading />
          ) : currentTab === "activity" && activityFeedError ? (
            <div className="text-red-600">
              <Banner
                visible
                variant="error"
                title="Error loading activity feed"
              />
            </div>
          ) : currentTab === "workouts" ? (
            <div className="mb-24">
              <div className="flex flex-wrap gap-2 justify-between sm:flex-row sm:items-center my-2">
                <div className="flex flex-wrap gap-2 sm:gap-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-gray-600">
                      From:
                    </span>
                    <InlineDatePicker
                      value={workoutDateRange.from}
                      onChange={(date) => {
                        const newDate = new Date(date);
                        newDate.setHours(0, 0, 0, 0);
                        setWorkoutDateRange((prev) => {
                          if (newDate > prev.to) {
                            const newTo = new Date(newDate);
                            newTo.setHours(23, 59, 59, 999);
                            return { from: newDate, to: newTo };
                          }
                          return { ...prev, from: newDate };
                        });
                      }}
                      displayClassName="text-xs sm:text-sm"
                      isSimple
                    />
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm text-gray-600">
                      To:
                    </span>
                    <InlineDatePicker
                      value={workoutDateRange.to}
                      onChange={(date) => {
                        const newDate = new Date(date);
                        newDate.setHours(23, 59, 59, 999);
                        setWorkoutDateRange((prev) => {
                          if (newDate < prev.from) {
                            const newFrom = new Date(newDate);
                            newFrom.setHours(0, 0, 0, 0);
                            return { from: newDate, to: newFrom };
                          }
                          return { ...prev, to: newDate };
                        });
                      }}
                      displayClassName="text-xs sm:text-sm"
                      isSimple
                    />
                  </div>
                </div>
                <ToggleButton
                  variant="primary"
                  className="text-sm"
                  onClick={() =>
                    setWorkoutStatus((prev) => (prev ? undefined : "active"))
                  }
                  selected={workoutStatus === "active"}
                >
                  {workoutStatus === "active"
                    ? "Show All Workouts"
                    : "Only Show Active Workouts"}
                </ToggleButton>
              </div>
              <WorkoutList
                workouts={workouts}
                exercises={exercises}
                timeFormat={profile?.preferred_time_format || "12h"}
                pagination={workoutPagination}
                onPageChange={setCurrentWorkoutPage}
              />
            </div>
          ) : currentTab === "routines" ? (
            <div className="mb-24">
              <div className="flex justify-between">
                <TextInput
                  title="Search"
                  placeholder="Search for routine..."
                  value={routinesSearchQuery}
                  onChange={(e) => setRoutinesSearchQuery(e.target.value)}
                />
                {sharedRoutines && sharedRoutines.length > 0 && (
                  <button
                    className="m-2"
                    onClick={() => setShowSharedRoutines((prev) => !prev)}
                  >
                    <div
                      className={`h-fit relative text-gray-600 flex flex-row gap-2 mr-2 ${showSharedRoutines ? "text-primary" : ""}`}
                    >
                      <span className="text-xs hidden sm:block">
                        Shared With You
                      </span>
                      <div className="flex items-center justify-center p-2">
                        <IoPeopleOutline
                          className={`absolute self-center ${!showSharedRoutines ? "animate-ping" : ""}`}
                        />
                        <IoPeopleOutline
                          className={`absolute self-center animate-pulse ${showSharedRoutines ? "hidden" : "block"}`}
                        />
                      </div>
                    </div>
                  </button>
                )}
              </div>
              {showSharedRoutines ? (
                <SharedRoutineList
                  friends={friends}
                  isQueried={routinesSearchQuery !== ""}
                  untilRoutinesLimit={untilRoutinesLimit}
                  untilExercisesLimit={untilExercisesLimit}
                  routines={queriedSharedRoutines}
                  exercises={exercises}
                  goToRoutines={() => setShowSharedRoutines(false)}
                />
              ) : (
                <RoutineList
                  friends={friends}
                  isQueried={routinesSearchQuery !== ""}
                  untilLimit={untilRoutinesLimit}
                  routines={queriedRoutines}
                  exercises={exercises}
                  preferredVisibility={profile?.preferred_visibility}
                />
              )}
            </div>
          ) : currentTab === "exercises" ? (
            <div className="mb-24">
              {selectedExercises.length > 0 && (
                <div
                  ref={exerciseActionsRef}
                  className="flex flex-row gap-4 mb-4"
                >
                  <Button
                    onClick={() => {
                      const exercise = exercises.find(
                        (ex) => ex.id === selectedExercises[0],
                      );
                      if (isNotNullish(exercise)) {
                        openDialog("edit-exercise", {
                          exercise: exercise,
                        });
                      }
                    }}
                    variant="submit"
                    disabled={selectedExercises.length !== 1}
                  >
                    Edit
                  </Button>

                  <Button
                    onClick={() => {
                      const exercise1 = exercises.find(
                        (ex) => ex.id === selectedExercises[0],
                      );
                      const exercise2 = exercises.find(
                        (ex) => ex.id === selectedExercises[1],
                      );
                      if (isNotNullish(exercise1) && isNotNullish(exercise2)) {
                        openDialog("merge-exercises", {
                          exercise1: exercise1,
                          exercise2: exercise2,
                        });
                      }
                    }}
                    variant="submit"
                    disabled={selectedExercises.length !== 2}
                  >
                    Merge
                  </Button>
                </div>
              )}
              {selectedExercises.length === 0 && (
                <div className="flex items-center mr-4">
                  <TextInput
                    title="Search"
                    placeholder="Search for exercise..."
                    onChange={(e) => setExercisesSearchQuery(e.target.value)}
                  />
                </div>
              )}
              <ExerciseLibrary
                untilLimit={untilExercisesLimit}
                isQueried={exercisesSearchQuery !== ""}
                exercises={queriedExercises}
                preferredResistanceUnit={profile?.preferred_resistance_unit}
                onSelectExercise={handleExerciseClick}
                selectedExercises={selectedExercises}
                deselectExercises={() => setSelectedExercises([])}
                actionsRef={exerciseActionsRef}
              />
            </div>
          ) : currentTab === "activity" ? (
            <div className="mb-24">
              <ActivityFeed
                activityFeed={activityFeed}
                friends={friends}
                profile={profile}
                pagination={activityFeedPagination}
                timeFormat={profile?.preferred_time_format}
                onPageChange={setCurrentActivityFeedPage}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export { HomePage };
